import { useEffect, useMemo, useState } from "react";
import Table from "examples/Tables/Table";
import { GuestProfileName } from "components/GuestProfileDrawer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";
import Avatar from "@mui/material/Avatar";
import dayjs from "utils/dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import NoResults from "components/NoResult";
import { FaStickyNote, FaEllipsisV, FaUser, FaClipboardList, FaTrash } from "react-icons/fa";
import { IconButton, Menu, MenuItem } from "@mui/material";
import Divider from "assets/theme/components/divider";
import GuestDetailsDialog from "./GuestDetailsDialog";
import VuiButton from "components/VuiButton";
import useCommon from "hooks/useCommon";
import GuestNotesDialog from "./GuestNotesDialog";
import DeleteGuestDialog from "./DeleteGuestDialog";
import {
  deleteGuest,
  getGuestLibrary,
  resetDeleteGuestState,
} from "../../../../store/slice/guestSlice";
import usePermissions from "hooks/usePermissions";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const menuItemSX = {
  borderRadius: "8px",
  mx: 0.5,
  my: 0.3,
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: "rgba(0, 76, 127)",
    // background: "linear-gradient(135deg, rgba(0,76,127,0.45), rgba(0,76,127,0.18))",
    // boxShadow: "inset 0 0 0 1px rgba(0,76,127,0.55)",
  },
};

const { dark, gradients, light } = colors;

const GuestCell = ({ guest }) => {
  const initials =
    guest.name
      ?.split(" ")
      ?.map((p) => p[0])
      ?.join("")
      ?.toUpperCase() || "G";

  return (
    <VuiBox display="flex" alignItems="center" gap={1.25}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          border: `0.5px solid ${rgba(dark.main, 0.25)}`,
          background: light.secondary,
        }}
      >
        {initials}
      </Avatar>

      <VuiBox display="flex" flexDirection="column" lineHeight={1.2}>
        <GuestProfileName guestId={guest.id} name={guest.name} sx={{ fontSize: 14 }} />

        <VuiTypography variant="caption" color="primary">
          {guest.email || ""}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
};

const StaysCell = ({ stays }) => <VuiTypography variant="caption">{stays} stays</VuiTypography>;

const PhoneCell = ({ phone }) => <VuiTypography variant="caption">{phone || "—"}</VuiTypography>;

const LastStayCell = ({ date }) => {
  if (!date) return "—";

  return (
    <VuiBox display="flex" flexDirection="column" lineHeight={1.2}>
      <VuiTypography variant="caption" mb={0.5}>
        {dayjs.tz(date).format("MMM D")}
      </VuiTypography>
      <VuiTypography variant="caption">{dayjs.tz(date).fromNow()}</VuiTypography>
    </VuiBox>
  );
};

const PreferencesCell = ({ percentage, completed, total }) => {
  const getColor = () => {
    if (percentage >= 100) return "success";
    if (percentage >= 80) return "warning";
    if (percentage >= 50) return "success";
    if (percentage >= 25) return "info";
    return "error";
  };
  return (
    <VuiBox minWidth={80}>
      <VuiProgress value={percentage} color={getColor()} />
      <VuiTypography variant="caption" sx={{ opacity: 0.75 }}>
        {percentage}%
      </VuiTypography>
    </VuiBox>
  );
};

const GuestTypeCell = ({ type, status }) => {
  const config = {
    Frequent: {
      bg: "rgba(0, 76, 127, 0.18)",
      border: "rgba(0, 76, 127, 0.45)",
      color: "#4fc3f7",
    },
    Returning: {
      bg: "rgba(0, 150, 136, 0.18)",
      border: "rgba(0, 150, 136, 0.45)",
      color: "#4db6ac",
    },
    New: {
      bg: "rgba(255,255,255,0.08)",
      border: "rgba(255,255,255,0.18)",
      color: "#b0bec5",
    },
  };

  const style = config[type] || config.New;

  return (
    <VuiBox display="flex" justifyContent="center">
      <VuiBox
        px={1.4}
        py={0.45}
        borderRadius="6px"
        sx={{
          background: style.bg,
          border: `1px solid ${style.border}`,
          color: style.color,
          fontSize: "10px",
          fontWeight: 500,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          backdropFilter: "blur(6px)",
        }}
      >
        {type}
      </VuiBox>
    </VuiBox>
  );
};

const NotesCell = ({ count, onClick }) => {
  const hasNotes = count > 0;

  return (
    <VuiBox display="flex" justifyContent="center">
      <VuiBox
        onClick={onClick}
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        px={1.1}
        py={0.4}
        borderRadius="8px"
        sx={{
          cursor: "pointer",
          background: hasNotes ? light.secondary : light.main,
          border: hasNotes
            ? `1px solid ${rgba(dark.main, 0.25)}`
            : `1px solid ${rgba(dark.main, 0.15)}`,
          transition: "all 0.2s ease",
          "&:hover": {
            background: dark.main,
          },
        }}
      >
        <FaStickyNote size={12} color={hasNotes ? "#ffffff" : "rgba(255,255,255,0.6)"} />

        <VuiTypography
          variant="caption"
          sx={{
            color: hasNotes ? "#ffffff" : "rgba(255,255,255,0.6)",
            minWidth: 10,
            textAlign: "center",
          }}
        >
          {count}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
};

const ActionButtonsCell = ({ guest, onDelete }) => {
  const { NAVIGATE } = useCommon();
  const { can } = usePermissions();

  return (
    <VuiBox display="flex" justifyContent="center" gap={1}>
      <VuiButton
        variant="outlined"
        color="primary"
        size="small"
        sx={{ px: 2, fontWeight: "regular" }}
        startIcon={<FaUser size={10} />}
        onClick={() => NAVIGATE(`/guestLibrary/profile-history/${guest.id}`)}
      >
        Details
      </VuiButton>

      {can("deleteGuests") && (
        <VuiButton
          variant="outlined"
          color="error"
          size="small"
          sx={{ px: 1 }}
          onClick={() => onDelete(guest)}
        >
          <FaTrash size={10} />
        </VuiButton>
      )}
    </VuiBox>
  );
};

const GuestLibraryTable = ({ search, handleNotify }) => {
  const { DISPATCH, STORE } = useCommon();

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const { guestLibrary, guestLibraryLoading, guestLibraryError, guestLibraryTotal } = STORE(
    (state) => state.guests
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);

  const handleOpenDetails = (guest) => {
    setSelectedGuest(guest);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedGuest(null);
  };

  const handleOpenNotes = (guest) => {
    setSelectedGuest(guest);
    setNotesOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesOpen(false);
    setSelectedGuest(null);
  };

  const handleOpenDelete = (guest) => {
    setGuestToDelete(guest);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setGuestToDelete(null);
  };

  const handleConfirmDelete = async (guest) => {
    if (!guest?.id) return;

    try {
      await DISPATCH(deleteGuest(guest.id)).unwrap();

      handleNotify?.({
        color: "success",
        title: "Deletion Started",
        content: "Guest was removed successfully. Permanent deletion is processing in background.",
        icon: "check",
      });

      handleCloseDelete();
      DISPATCH(resetDeleteGuestState());
    } catch (error) {
      handleNotify?.({
        color: "error",
        title: "Delete Failed",
        content: error || "Unable to delete guest.",
        icon: "warning",
      });
    }
  };

  useEffect(() => {
    if (!hotelIds?.length) return;

    const payload = {
      hotelIds,
      page: page + 1,
      pageSize: rowsPerPage,
    };

    if (debouncedSearch?.length >= 3) {
      payload.search = debouncedSearch;
    }

    DISPATCH(getGuestLibrary(payload));
  }, [hotelIds, page, rowsPerPage, debouncedSearch, DISPATCH]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = search?.trim() || "";

      if (trimmed.length >= 3 || trimmed.length === 0) {
        setDebouncedSearch(trimmed);
        setPage(0);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const tableData = useMemo(
    () => ({
      columns: [
        { name: "guest", align: "left" },
        { name: "phone", align: "center" },
        { name: "stays", align: "center" },
        { name: "lastStay", align: "center" },
        { name: "reponse_rate", align: "center" },
        // { name: "type", align: "center" },
        { name: "notes", align: "center" },
        { name: "actions", align: "center" },
      ],
      rows: (guestLibrary || []).map((g) => {
        const guest = {
          id: g.guestId,
          name: g.name,
          email: g.email,
          phone: g.phone,
          stays: g.totalStays || 0,
          lastStay: g.lastStay?.checkOutDate || g.lastStay?.checkInDate || null,
          responseRate: g.avgResponseRatePct || 0,
          notesCount: g.notesCount || 0,
        };

        return {
          guest: <GuestCell guest={guest} />,
          phone: <PhoneCell phone={guest.phone} />,
          stays: <StaysCell stays={guest.stays} />,
          lastStay: <LastStayCell date={guest.lastStay} />,
          reponse_rate: (
            <PreferencesCell
              percentage={guest.responseRate}
              completed={guest.responseRate}
              total={100}
            />
          ),
          notes: <NotesCell count={guest.notesCount} onClick={() => handleOpenNotes(guest)} />,
          actions: <ActionButtonsCell guest={guest} onDelete={handleOpenDelete} />,
        };
      }),
    }),
    [guestLibrary, handleOpenNotes, handleOpenDelete]
  );

  return !guestLibraryLoading && (guestLibrary || []).length === 0 ? (
    <NoResults title="No Guests Found" description="No guest data available." />
  ) : (
    <>
      <Table
        columns={tableData.columns}
        rows={tableData.rows}
        pagination
        loading={guestLibraryLoading}
        paginationMode="server"
        page={page}
        rowsPerPage={rowsPerPage}
        total={guestLibraryTotal}
        onPageChange={(newPage) => setPage(newPage)}
        onRowsPerPageChange={(newLimit) => {
          setRowsPerPage(newLimit);
          setPage(0);
        }}
      />

      <GuestNotesDialog open={notesOpen} onClose={handleCloseNotes} guest={selectedGuest} />

      <DeleteGuestDialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        guest={guestToDelete}
      />

      <GuestDetailsDialog open={detailsOpen} onClose={handleCloseDetails} guest={selectedGuest} />
    </>
  );
};

export default GuestLibraryTable;
